import { useState, useMemo, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ArrowRight, MapPin, Sofa, Home, Package2, Boxes, Layers,
  Store, PackageOpen, MoreHorizontal, Upload, X, Check, MessageCircle, Loader2, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { waLink, COMPANY, CARGO } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CARGO_ICONS = {
  "Möbel": Sofa,
  Umzug: Home,
  "Einzelstück": Package2,
  Kartons: PackageOpen,
  Paletten: Layers,
  "Gewerbliche Waren": Store,
  Sperrgut: Boxes,
  Sonstiges: MoreHorizontal,
};

const AMOUNT = ["1–3 Teile", "4–10 Teile", "Halber Transporter", "Voller Transporter", "LKW nötig", "Weiß ich nicht"];
const FLOORS = ["Erdgeschoss", "1. Etage", "2. Etage", "3. Etage", "4. Etage", "5. oder höher"];
const ELEVATOR = ["Ja", "Nein", "Nur Abholort", "Nur Lieferort"];

const STEPS = [
  "Was möchten Sie transportieren?",
  "Wo soll abgeholt werden?",
  "Wo soll geliefert werden?",
  "Umfang & Termin",
  "Ihre Kontaktdaten",
];

const EMPTY = {
  cargo_type: "", from_location: "", to_location: "", move_date: "", volume: "",
  floor_from: "", floor_to: "", elevator: "", message: "",
  name: "", phone: "", email: "", photos: [], consent: false,
};

const inputCls =
  "w-full bg-[#0f0f0f] border border-white/15 focus:border-[#f95700] focus:outline-none text-white placeholder:text-neutral-600 h-14 px-4 text-base transition-colors";

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="eyebrow text-neutral-500 block mb-2">{label}</span>
    {children}
  </label>
);

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`px-4 py-3 text-sm font-semibold border transition-all duration-300 text-left ${
      active ? "bg-[#f95700] border-[#f95700] text-white" : "bg-[#0f0f0f] border-white/15 text-neutral-300 hover:border-white/40"
    }`}
  >
    {children}
  </button>
);

export const Configurator = () => {
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [d, setD] = useState(EMPTY);

  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));

  const canNext = useMemo(() => {
    if (step === 0) return !!d.cargo_type;
    if (step === 1) return d.from_location.trim().length > 1;
    if (step === 2) return d.to_location.trim().length > 1;
    if (step === 3) return true;
    return d.name.trim() && d.phone.trim() && d.consent;
  }, [step, d]);

  const summary = useMemo(() => {
    const lines = [
      `*Transportanfrage – ${COMPANY.name}*`,
      `Anliegen: ${d.cargo_type || "-"}`,
      `Abholung: ${d.from_location || "-"}`,
      `Lieferung: ${d.to_location || "-"}`,
      d.move_date && `Wunschtermin: ${d.move_date}`,
      d.volume && `Umfang: ${d.volume}`,
      d.floor_from && `Etage Abholort: ${d.floor_from}`,
      d.floor_to && `Etage Lieferort: ${d.floor_to}`,
      d.elevator && `Aufzug: ${d.elevator}`,
      d.message && `Infos: ${d.message}`,
      `Name: ${d.name || "-"}`,
      `Telefon: ${d.phone || "-"}`,
      d.email && `E-Mail: ${d.email}`,
      d.photos.length && `Fotos: ${d.photos.map((p) => `${process.env.REACT_APP_BACKEND_URL}${p}`).join(" | ")}`,
    ].filter(Boolean);
    return lines.join("\n");
  }, [d]);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const res = await axios.post(`${API}/upload`, fd);
        urls.push(res.data.url);
      }
      setD((p) => ({ ...p, photos: [...p.photos, ...urls].slice(0, 5) }));
      toast.success(`${urls.length} Foto(s) hochgeladen`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, { ...d, kind: "konfigurator", service: d.cargo_type });
      setDone(true);
      toast.success("Ihre Anfrage ist bereit – jetzt per WhatsApp senden.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Anfrage konnte nicht gespeichert werden.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="konfigurator" data-testid="configurator-section" className="relative w-full bg-[#0a0a0a] grain py-20 sm:py-24 lg:py-32">
      <div className="w-full px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-16">
          <div className="lg:col-span-4">
            <Reveal>
              <Eyebrow chapter="04">Transport-Konfigurator</Eyebrow>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display uppercase font-black leading-[0.9] tracking-tight text-white text-[clamp(2.1rem,7vw,4.4rem)]">
                Transport<br />
                <span className="text-[#f95700]">anfragen</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-5 text-[15px] sm:text-base text-neutral-400 leading-relaxed max-w-md">
                In fünf kurzen Schritten zu einem belastbaren Angebot. Je genauer Ihre Angaben,
                desto präziser können wir kalkulieren.
              </p>
            </Reveal>

            <Reveal delay={0.15} className="mt-8 hidden lg:block space-y-1">
              {STEPS.map((s, i) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => !done && i <= step && setStep(i)}
                  data-testid={`configurator-step-nav-${i + 1}`}
                  className={`w-full flex items-center gap-4 px-4 py-3 border-l-2 text-left transition-colors ${
                    i === step && !done ? "border-[#f95700] bg-white/5" : i < step || done ? "border-[#f95700]/40" : "border-white/10"
                  }`}
                >
                  <span
                    className={`font-mono-t text-[11px] w-6 h-6 grid place-items-center shrink-0 ${
                      i < step || done ? "bg-[#f95700] text-white" : i === step ? "bg-white text-black" : "bg-white/10 text-neutral-500"
                    }`}
                  >
                    {i < step || done ? <Check size={12} strokeWidth={3} /> : i + 1}
                  </span>
                  <span className={`font-display text-[15px] font-semibold uppercase tracking-wide leading-tight ${i === step && !done ? "text-white" : "text-neutral-500"}`}>
                    {s}
                  </span>
                </button>
              ))}
            </Reveal>
          </div>

          <div className="lg:col-span-8">
            <Reveal delay={0.1} className="border border-white/12 bg-[#121212] relative">
              <div className="h-[3px] bg-white/10">
                <motion.div
                  className="h-full bg-[#f95700]"
                  animate={{ width: done ? "100%" : `${((step + 1) / 5) * 100}%` }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                />
              </div>

              <div className="p-5 sm:p-8 lg:p-10 min-h-[430px] flex flex-col">
                <AnimatePresence mode="wait">
                  {done ? (
                    <motion.div
                      key="done"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="flex-1 flex flex-col justify-center"
                      data-testid="configurator-success"
                    >
                      <CheckCircle2 size={48} className="text-[#f95700] mb-5" />
                      <h3 className="font-display text-[clamp(1.9rem,7vw,3rem)] font-black uppercase text-white leading-none">
                        Ihre Anfrage ist bereit
                      </h3>
                      <p className="mt-4 text-[15px] sm:text-base text-neutral-400 max-w-xl leading-relaxed">
                        Wir haben Ihre Angaben erfasst. Senden Sie sie jetzt mit einem Klick per
                        WhatsApp – oder rufen Sie direkt an. Sie erhalten anschließend ein
                        individuelles Angebot.
                      </p>
                      <div className="mt-7 flex flex-col sm:flex-row gap-3">
                        <a
                          href={waLink(summary)}
                          target="_blank"
                          rel="noreferrer"
                          data-testid="configurator-whatsapp-send"
                          className="inline-flex items-center justify-center gap-3 bg-[#f95700] hover:bg-[#e04e00] text-white h-16 px-7 font-display text-base sm:text-lg font-bold uppercase tracking-wide transition-colors"
                        >
                          <MessageCircle size={20} />
                          Per WhatsApp senden
                        </a>
                        <a
                          href={`tel:${COMPANY.phoneRaw}`}
                          data-testid="configurator-call"
                          className="inline-flex items-center justify-center gap-3 border border-white/25 hover:border-white text-white h-16 px-7 font-display text-base sm:text-lg font-bold uppercase tracking-wide transition-colors"
                        >
                          Anrufen
                        </a>
                      </div>
                      <button
                        type="button"
                        onClick={() => { setDone(false); setStep(0); setD(EMPTY); }}
                        data-testid="configurator-reset"
                        className="mt-7 self-start font-mono-t text-xs uppercase tracking-widest text-neutral-500 hover:text-[#f95700] transition-colors"
                      >
                        Neue Anfrage starten
                      </button>
                    </motion.div>
                  ) : (
                    <motion.div
                      key={step}
                      initial={{ opacity: 0, x: 24 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -24 }}
                      transition={{ duration: 0.42, ease: [0.16, 1, 0.3, 1] }}
                      className="flex-1"
                    >
                      <div className="flex items-baseline gap-3 mb-6">
                        <span className="font-mono-t text-xs text-[#f95700]">Schritt {step + 1} / 5</span>
                        <span className="hairline flex-1" />
                      </div>
                      <h3 className="font-display text-[clamp(1.6rem,5.5vw,2.4rem)] font-black uppercase text-white leading-[1.05] mb-7">
                        {STEPS[step]}
                      </h3>

                      {step === 0 && (
                        <div className="grid grid-cols-2 xl:grid-cols-4 gap-2.5">
                          {CARGO.map((c) => {
                            const Icon = CARGO_ICONS[c.v] || MoreHorizontal;
                            const active = d.cargo_type === c.v;
                            return (
                              <button
                                key={c.v}
                                type="button"
                                onClick={() => set("cargo_type", c.v)}
                                data-testid={`cargo-option-${c.v}`}
                                className={`flex flex-col items-start gap-3 p-4 sm:p-5 border transition-all duration-300 ${
                                  active ? "border-[#f95700] bg-[#f95700]/10" : "border-white/15 bg-[#0f0f0f] hover:border-white/40"
                                }`}
                              >
                                <Icon size={22} className={active ? "text-[#f95700]" : "text-neutral-400"} />
                                <span className="font-display text-base sm:text-lg font-bold uppercase text-white text-left leading-tight">
                                  {c.v}
                                </span>
                                <span className="font-mono-t text-[10px] text-neutral-500 text-left leading-snug">{c.d}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {step === 1 && (
                        <div className="space-y-6 max-w-xl">
                          <Field label="Adresse / PLZ / Ort (Abholung)" testid="field-from">
                            <div className="relative">
                              <MapPin size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#f95700]" />
                              <input
                                data-testid="input-from-location"
                                className={`${inputCls} pl-12`}
                                placeholder="z. B. Große Weidestraße 2, 28195 Bremen"
                                value={d.from_location}
                                onChange={(e) => set("from_location", e.target.value)}
                              />
                            </div>
                          </Field>
                          <Field label="Etage am Abholort (optional)" testid="field-floor-from">
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                              {FLOORS.map((f) => (
                                <Chip key={f} active={d.floor_from === f} onClick={() => set("floor_from", f)} testid={`chip-floor-from-${f}`}>
                                  {f}
                                </Chip>
                              ))}
                            </div>
                          </Field>
                        </div>
                      )}

                      {step === 2 && (
                        <div className="space-y-6 max-w-xl">
                          <Field label="Adresse / PLZ / Ort (Lieferung)" testid="field-to">
                            <div className="relative">
                              <MapPin size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#f95700]" />
                              <input
                                data-testid="input-to-location"
                                className={`${inputCls} pl-12`}
                                placeholder="z. B. 20095 Hamburg"
                                value={d.to_location}
                                onChange={(e) => set("to_location", e.target.value)}
                              />
                            </div>
                          </Field>
                          <Field label="Etage am Lieferort (optional)" testid="field-floor-to">
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                              {FLOORS.map((f) => (
                                <Chip key={f} active={d.floor_to === f} onClick={() => set("floor_to", f)} testid={`chip-floor-to-${f}`}>
                                  {f}
                                </Chip>
                              ))}
                            </div>
                          </Field>
                        </div>
                      )}

                      {step === 3 && (
                        <div className="space-y-6">
                          <div className="grid sm:grid-cols-2 gap-6">
                            <Field label="Gewünschtes Datum" testid="field-date">
                              <input
                                type="date"
                                data-testid="input-move-date"
                                className={inputCls}
                                value={d.move_date}
                                onChange={(e) => set("move_date", e.target.value)}
                              />
                            </Field>
                            <Field label="Aufzug vorhanden?" testid="field-elevator">
                              <div className="grid grid-cols-2 gap-2">
                                {ELEVATOR.map((v) => (
                                  <Chip key={v} active={d.elevator === v} onClick={() => set("elevator", v)} testid={`chip-elevator-${v}`}>
                                    {v}
                                  </Chip>
                                ))}
                              </div>
                            </Field>
                          </div>
                          <Field label="Menge / Umfang" testid="field-volume">
                            <div className="flex flex-wrap gap-2">
                              {AMOUNT.map((v) => (
                                <Chip key={v} active={d.volume === v} onClick={() => set("volume", v)} testid={`chip-volume-${v}`}>
                                  {v}
                                </Chip>
                              ))}
                            </div>
                          </Field>
                          <Field label="Zusätzliche Informationen" testid="field-message">
                            <textarea
                              data-testid="input-message"
                              rows={4}
                              className={`${inputCls} h-auto py-3 resize-none`}
                              placeholder="Maße, Gewicht, enge Treppen, Parksituation, Halteverbotszone ..."
                              value={d.message}
                              onChange={(e) => set("message", e.target.value)}
                            />
                          </Field>
                        </div>
                      )}

                      {step === 4 && (
                        <div className="space-y-5">
                          <div className="grid sm:grid-cols-2 gap-5">
                            <Field label="Name *" testid="field-name">
                              <input data-testid="input-name" className={inputCls} placeholder="Vor- und Nachname" value={d.name} onChange={(e) => set("name", e.target.value)} />
                            </Field>
                            <Field label="Telefon *" testid="field-phone">
                              <input data-testid="input-phone" className={inputCls} placeholder="0176 ..." value={d.phone} onChange={(e) => set("phone", e.target.value)} />
                            </Field>
                          </div>
                          <Field label="E-Mail (optional)" testid="field-email">
                            <input data-testid="input-email" className={inputCls} placeholder="name@beispiel.de" value={d.email} onChange={(e) => set("email", e.target.value)} />
                          </Field>

                          <div>
                            <span className="eyebrow text-neutral-500 block mb-2">Fotos hochladen (optional, max. 5)</span>
                            <button
                              type="button"
                              onClick={() => fileRef.current?.click()}
                              disabled={uploading || d.photos.length >= 5}
                              data-testid="upload-photos-button"
                              className="w-full border border-dashed border-white/20 hover:border-[#f95700] disabled:opacity-50 py-6 flex flex-col items-center gap-2 transition-colors"
                            >
                              {uploading ? <Loader2 size={22} className="text-[#f95700] animate-spin" /> : <Upload size={22} className="text-[#f95700]" />}
                              <span className="font-display text-[13px] sm:text-sm font-bold uppercase tracking-wider text-neutral-300 text-center px-4">
                                {uploading ? "Wird hochgeladen ..." : "Bilder auswählen (JPG, PNG, max. 8 MB)"}
                              </span>
                            </button>
                            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onFiles} data-testid="photo-file-input" />
                            {d.photos.length > 0 && (
                              <div className="mt-4 flex flex-wrap gap-3" data-testid="photo-previews">
                                {d.photos.map((p) => (
                                  <div key={p} className="relative w-20 h-20 sm:w-24 sm:h-24">
                                    <img src={`${process.env.REACT_APP_BACKEND_URL}${p}`} alt="Upload" className="w-full h-full object-cover border border-white/15" />
                                    <button
                                      type="button"
                                      onClick={() => set("photos", d.photos.filter((x) => x !== p))}
                                      className="absolute -top-2 -right-2 w-6 h-6 bg-[#f95700] grid place-items-center"
                                      aria-label="Foto entfernen"
                                    >
                                      <X size={13} className="text-white" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-wrapper">
                            <input
                              type="checkbox"
                              checked={d.consent}
                              onChange={(e) => set("consent", e.target.checked)}
                              data-testid="consent-checkbox"
                              className="mt-1 w-5 h-5 accent-[#f95700] shrink-0"
                            />
                            <span className="text-[13px] text-neutral-400 leading-relaxed">
                              Ich bin damit einverstanden, dass meine Angaben zur Bearbeitung der Anfrage
                              gespeichert und verarbeitet werden. Beim Versand über WhatsApp werden die Daten
                              an WhatsApp Ireland Ltd. übertragen. Details in der{" "}
                              <a href="/datenschutz" target="_blank" rel="noreferrer" className="text-[#f95700] underline">
                                Datenschutzerklärung
                              </a>
                              .
                            </span>
                          </label>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>

                {!done && (
                  <div className="mt-8 pt-5 border-t border-white/10 flex items-center justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => setStep((s) => Math.max(0, s - 1))}
                      disabled={step === 0}
                      data-testid="configurator-back-button"
                      className="inline-flex items-center gap-2 h-14 px-5 sm:px-6 border border-white/20 disabled:opacity-30 hover:border-white font-display font-bold uppercase tracking-wide text-white transition-colors"
                    >
                      <ArrowLeft size={18} /> <span className="hidden sm:inline">Zurück</span>
                    </button>
                    {step < 4 ? (
                      <button
                        type="button"
                        onClick={() => canNext && setStep((s) => s + 1)}
                        disabled={!canNext}
                        data-testid="configurator-next-button"
                        className="inline-flex items-center gap-3 h-14 px-6 sm:px-8 bg-[#f95700] hover:bg-[#e04e00] disabled:opacity-40 disabled:hover:bg-[#f95700] font-display text-base sm:text-lg font-bold uppercase tracking-wide text-white transition-colors"
                      >
                        Weiter <ArrowRight size={18} />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={submit}
                        disabled={!canNext || sending}
                        data-testid="configurator-submit-button"
                        className="inline-flex items-center gap-3 h-14 px-6 sm:px-8 bg-[#f95700] hover:bg-[#e04e00] disabled:opacity-40 font-display text-base sm:text-lg font-bold uppercase tracking-wide text-white transition-colors"
                      >
                        {sending ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
                        Transport anfragen
                      </button>
                    )}
                  </div>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
